import React, { useState } from "react";
import { ShieldCheck, Copy, Download, AlertTriangle, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startTotpSetup, totpQrUrl, confirmTotpSetup } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const apiError = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

// Full TOTP enrollment flow: intro -> QR + manual key + confirmation code ->
// one-time backup codes. Used from the Sécurité card (optional) and from the
// forced page for admins past the grace period (mandatory).
export const TwoFactorSetup = ({ mandatory = false, onEnabled }) => {
  const [step, setStep] = useState("intro"); // intro | scan | backup
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const start = async () => {
    if (pending) return;
    setError("");
    setPending(true);
    try {
      const r = await startTotpSetup();
      setManualKey(r.manual_key);
      setStep("scan");
    } catch (err) {
      setError(apiError(err, "Impossible de démarrer la configuration."));
    } finally {
      setPending(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    if (pending || code.length !== 6) return;
    setError("");
    setPending(true);
    try {
      const r = await confirmTotpSetup(code);
      setBackupCodes(r.backup_codes);
      setStep("backup");
    } catch (err) {
      setError(apiError(err, "Code incorrect. Réessayez."));
      setCode("");
    } finally {
      setPending(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(manualKey.replace(/ /g, ""));
    toast.success("Clé copiée");
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText(backupCodes.join("\n"));
    toast.success("Codes copiés");
  };

  const downloadCodes = () => {
    const text = [
      "Codes de secours — Standard Call",
      "Chaque code ne fonctionne qu'une seule fois.",
      "Conservez-les dans votre gestionnaire de mots de passe.",
      "",
      ...backupCodes,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "codes-de-secours-reco-pneus.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (step === "backup") {
    return (
      <div className="space-y-4" data-testid="totp-backup-step">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="totp-backup-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Conservez ces codes dans votre gestionnaire de mots de passe : ils sont le seul moyen d'accéder au compte si vous perdez votre téléphone. Ils ne seront affichés qu'une seule fois.</span>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="totp-backup-codes">
          {backupCodes.map((c) => (
            <code key={c} className="text-sm font-mono text-slate-800 text-center tabular-nums">{c}</code>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="totp-copy-backup" onClick={copyCodes} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 transition-colors">
            <Copy className="h-4 w-4" /> Copier
          </button>
          <button type="button" data-testid="totp-download-backup" onClick={downloadCodes} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 transition-colors">
            <Download className="h-4 w-4" /> Télécharger
          </button>
        </div>
        <button
          type="button"
          data-testid="totp-finish-button"
          onClick={() => { toast.success("Double authentification activée"); onEnabled?.(); }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] transition-all"
        >
          <ShieldCheck className="h-4 w-4" /> J'ai conservé mes codes — Terminer
        </button>
      </div>
    );
  }

  if (step === "scan") {
    return (
      <form onSubmit={confirm} className="space-y-4" data-testid="totp-scan-step">
        {error && (
          <div data-testid="totp-setup-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <p className="text-sm text-slate-500 leading-snug">
          Scannez ce QR code avec votre application d'authentification (Google Authenticator, 1Password, Bitwarden…).
        </p>
        <div className="flex justify-center">
          <img
            src={totpQrUrl()}
            alt="QR code de configuration de la double authentification"
            data-testid="totp-qr-image"
            className="h-44 w-44 rounded-lg border border-slate-200 bg-white p-2"
          />
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Saisie manuelle</p>
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm font-mono text-slate-800 break-all" data-testid="totp-manual-key">{manualKey}</code>
            <button type="button" data-testid="totp-copy-key" onClick={copyKey} aria-label="Copier la clé" className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-white shrink-0">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="totp-setup-code" className="text-sm">Code à 6 chiffres</Label>
          <Input
            id="totp-setup-code"
            data-testid="totp-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            className="mt-1 h-11 text-base text-center tracking-[0.4em] font-mono tabular-nums"
          />
        </div>
        <button
          type="submit"
          data-testid="totp-confirm-button"
          disabled={pending || code.length !== 6}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 transition-all"
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          {pending ? "Vérification…" : "Activer la double authentification"}
        </button>
      </form>
    );
  }

  return (
    <div className="space-y-4" data-testid="totp-intro-step">
      {mandatory && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="totp-mandatory-notice">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Le délai de 7 jours est écoulé : la double authentification est désormais obligatoire pour les administrateurs et les superviseurs.</span>
        </div>
      )}
      <p className="text-sm text-slate-500 leading-snug">
        La double authentification protège votre compte avec un code à 6 chiffres généré par votre téléphone, en plus de votre mot de passe.
      </p>
      {error && (
        <div data-testid="totp-setup-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <button
        type="button"
        data-testid="totp-setup-start-button"
        onClick={start}
        disabled={pending}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 transition-all"
      >
        {pending && <Loader2 className="h-4 w-4 animate-spin" />}
        <ShieldCheck className="h-4 w-4" /> Activer la double authentification
      </button>
    </div>
  );
};
